import React, { useEffect, useMemo, useState } from 'react';
import { Button, Image } from 'antd';
import type { ImageProps, ModalProps } from 'antd';
import { LinkOutlined } from '@ant-design/icons';

interface ImageViewerModalProps {
  imageUrls: string[] | string;
  open?: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  initialIndex?: number;
  title?: string;
  triggerText?: string;
  trigger?: React.ReactNode;
  triggerMode?: 'icon' | 'text' | 'none';
  disabled?: boolean;
  showCounter?: boolean;
  imageMaxHeight?: number;
  imageProps?: Omit<ImageProps, 'src' | 'alt'>;
  modalProps?: Omit<ModalProps, 'open' | 'onCancel' | 'title' | 'footer'>;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  imageUrls,
  open,
  onClose,
  onOpenChange,
  initialIndex = 0,
  title = 'Image Preview',
  triggerText = 'Open image',
  trigger,
  triggerMode = 'icon',
  disabled = false,
  showCounter = true,
  imageMaxHeight = 70, // Kept for backward compatibility
  imageProps = {},
  modalProps,
}) => {
  const images = useMemo(
    () =>
      (Array.isArray(imageUrls) ? imageUrls : [imageUrls])
        .map((url) => String(url || '').trim())
        .filter(Boolean),
    [imageUrls]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof open === 'boolean';
  const resolvedOpen = isControlled ? !!open : internalOpen;

  const safeSetOpen = (nextOpen: boolean) => {
    if (!isControlled) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const openViewer = () => {
    if (disabled || !images.length) return;
    safeSetOpen(true);
  };

  const handleClose = () => {
    safeSetOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (!resolvedOpen) return;
    if (!images.length) {
      setCurrentIndex(0);
      return;
    }
    const safeIndex = Math.min(Math.max(initialIndex, 0), images.length - 1);
    setCurrentIndex(safeIndex);
  }, [resolvedOpen, images, initialIndex]);

  const showDefaultTrigger = triggerMode !== 'none';

  const renderDefaultTrigger = () => {
    if (!showDefaultTrigger) return null;
    if (triggerMode === 'text') {
      return (
        <Button type="link" onClick={openViewer} className="p-0" title={triggerText} disabled={disabled || !images.length}>
          {triggerText}
        </Button>
      );
    }
    return (
      <Button
        type="text"
        onClick={openViewer}
        className="p-0 text-blue-600 hover:text-blue-800"
        title={triggerText}
        icon={<LinkOutlined />}
        disabled={disabled || !images.length}
      />
    );
  };

  return (
    <>
      {trigger ? (
        <button
          type="button"
          onClick={openViewer}
          className="p-0 bg-transparent border-0 cursor-pointer"
          disabled={disabled || !images.length}
          aria-label={triggerText}
          title={triggerText}
        >
          {trigger}
        </button>
      ) : (
        renderDefaultTrigger()
      )}

      <Image.PreviewGroup
        preview={{
          visible: resolvedOpen,
          current: currentIndex,
          onChange: (next) => setCurrentIndex(next),
          onVisibleChange: (nextVisible) => {
            if (!nextVisible) {
              handleClose();
              return;
            }
            safeSetOpen(true);
          },
          countRender: showCounter ? undefined : () => '',
          zIndex: modalProps?.zIndex,
          getContainer: modalProps?.getContainer as any,
        }}
      >
        {images.map((src, idx) => (
          <Image
            key={`${src}-${idx}`}
            src={src}
            alt={`${title} ${idx + 1}`}
            preview
            {...imageProps}
            style={{ display: 'none', maxHeight: `${imageMaxHeight}vh`, ...(imageProps?.style || {}) }}
          />
        ))}
      </Image.PreviewGroup>
    </>
  );
};

export default ImageViewerModal;
